import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import ArgonButton from 'component/Argon/ArgonButton';
import ArgonSelect from 'component/Argon/ArgonSelect';
import TextField from 'component/TextField';
import { AutoCompleteDistrict } from 'component/basicForm';
import BasicLayout from 'layout/BasicLayout';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { number, object, string } from 'yup';

import register from 'api/auth/register';
import useAuth from 'auth/hook/useAuth';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import InputAgreement from './components/InputAgreement';
import PasswordField from 'component/PasswordField';

const schema = object({
  company: string().required(),
  address1: string().required(),
  district: string().required(),
  area: string().required(),
  province: string().required(),
  zipcode: number().required().min(5),
  prefix: string().required(),
  name: string().required(),
  lastname: string().required(),
  idCard: string().required().min(13),
  phone: string().required().min(9),
  email: string().required().email(),
  password: string().required().min(8),
});

const defaultValues = {
  company: '',
  address1: '',
  district: '',
  area: '',
  province: '',
  zipcode: undefined,
  prefix: '',
  name: '',
  lastname: '',
  idCard: '',
  phone: '',
  email: '',
  password: '',
};

const PREFIX_OPTIONS = ['MR.', 'MS.', 'MRS.', 'MISS'].map((x) => ({ label: x, value: x }));

export default function Signup() {
  const { code } = useParams();
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const [agreement, setAgreemnet] = useState(false);

  const handleSetAgremment = () => setAgreemnet((prev) => !prev);

  const handleRegister = (data) =>
    register(data, code)
      .then((response) => {
        if (response.status === 200) {
          setAuth({ ...auth, username: data.email });
          navigate('/sign-in');
        }
      })
      .catch(console.error);

  const onSubmit = (data) => {
    const zipcode = `${data.zipcode}`;
    const body = { ...data, zipcode };
    if (!agreement) {
      Swal.fire({
        title: 'ยอมรับเงื่อนไขการบริการ?',
        text: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยอมรับ',
        cancelButtonText: 'ไม่ยอมรับ',
      }).then((result) => {
        if (result.isConfirmed) {
          setAgreemnet(true);
          handleRegister(body);
        }
        Swal.close();
      });
    } else {
      handleRegister(body);
    }
  };

  function onCommit(data) {
    if (data) {
      const { amphoe: area, province, zipcode } = data;
      setValue('area', area);
      setValue('province', province);
      setValue('zipcode', zipcode);
    }
  }

  return (
    <BasicLayout>
      <Card sx={{ overflow: 'visible' }}>
        <CardHeader sx={{ textAlign: 'center' }} title="ลงทะเบียน" subheader="กรอกข้อมูลเพื่อลงทะเบียนใช้บริการ" />
        <CardContent>
          <Box component="form" role="form">
            <Box>
              <Typography variant="button">ส่วนที่1: ข้อมูลผู้ประกอบการ</Typography>
              <TextInput
                dense
                name="company"
                placeholder="ชื่อ, ชื่อบริษัท/ชื่อสถานประกอบการ ตัวอย่าง. บริษัทจัดหางานดีจำกัด"
                control={control}
                error={errors.company}
              />
              <TextInput
                label="ที่อยู่สถานประกอบการ"
                name="address1"
                placeholder="*ที่อยู่ ตัวอย่าง. 99/99 หมู่ที่ 9"
                control={control}
                error={errors.address1}
              />
              <Box my={2}>
                <Controller
                  name="district"
                  control={control}
                  render={({ field }) => (
                    <AutoCompleteDistrict
                      name="district"
                      placeholder="ตำบล/แขวง"
                      onCommit={onCommit}
                      error={!!errors.district}
                      {...field}
                    />
                  )}
                />
              </Box>
              <TextInput name="area" placeholder="อำเภอ/เขต" disabled control={control} error={errors.area} />
              <TextInput name="province" placeholder="จังหวัด" disabled control={control} error={errors.province} />
              <TextInput name="zipcode" placeholder="รหัสไปรษณีย์" disabled control={control} error={errors.zipcode} />
              <Typography variant="button">ส่วนที่2: ข้อมูลส่วนตัว</Typography>
              <Box mb={2}>
                <SelectorInput
                  name="prefix"
                  placeholder="คำนำหน้า"
                  options={PREFIX_OPTIONS}
                  control={control}
                  error={errors.prefix}
                />
              </Box>
              <TextInput name="name" placeholder="ชื่อ ตัวอย่าง. วินัย" control={control} error={errors.name} />
              <TextInput name="lastname" placeholder="นามสกุล" control={control} error={errors.lastname} />
              <TextInput
                name="idCard"
                placeholder="เลขประจำตัวประชาชน (13 หลัก)"
                control={control}
                error={errors.idCard}
              />
              <TextInput name="phone" placeholder="เบอร์โทรติดต่อ" control={control} error={errors.phone} />
              <TextInput name="email" placeholder="อีเมล (สำหรับเข้าใช้งาน)" control={control} error={errors.email} />
              <Controller
                name="password"
                control={control}
                render={({ field }) => <PasswordField {...field} error={errors.password} />}
              />
            </Box>
            <Box my={2}>
              <InputAgreement agreement={agreement} setAgremment={handleSetAgremment} />
            </Box>
            <Box>
              <ArgonButton color="info" size="large" fullWidth onClick={handleSubmit(onSubmit)}>
                สมัคร
              </ArgonButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </BasicLayout>
  );
}

function TextInput({ control, error, name, placeholder, label, dense, disabled }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          name={name}
          placeholder={placeholder}
          label={label}
          dense={dense}
          disabled={disabled}
          size="large"
          error={error}
          {...field}
        />
      )}
    />
  );
}

function SelectorInput({ options, control, error, name, placeholder }) {
  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value, ref } }) => (
          <ArgonSelect
            ref={ref}
            placeholder={placeholder}
            error={!!error}
            value={options.filter((x) => x.value === value)}
            onChange={(v) => onChange(v.value)}
            options={options}
            // {...field}
          />
        )}
      />
      {!!error && (
        <Box ml={0.5} display="flex">
          <Typography component="label" color="red" variant="caption" fontWeight="bold">
            {`${error.message}`}
          </Typography>
        </Box>
      )}
    </>
  );
}
